import { useCallback, useEffect, useRef, useState } from "react";
import type { AutosaveResponse, StyleChips, WorkStep } from "@studio/shared";
import { apiRequest } from "./api";
import { retryOnAutosaveConflict } from "./autosaveConflict";
import { publishPendingGifts } from "./gifts";

const backupTtlMs = 24 * 60 * 60 * 1_000;

export interface AutosavePayload {
  step: WorkStep;
  mode?: "lyrics" | "humming" | undefined;
  topic?: string | undefined;
  lyrics?: string | undefined;
  intro?: string | undefined;
  chips?: Partial<StyleChips> | undefined;
  story_line?: string | undefined;
  art_direction?: string | undefined;
  scene_text?: Array<{ id: string; scene_text: string }> | undefined;
}

interface DraftBackup {
  expiresAt: number;
  payload: AutosavePayload;
}

export type AutosaveStatus = "idle" | "saving" | "saved" | "error";

function backupKey(workId: string): string {
  return `mvs:draft:${workId}:v1`;
}

export function loadDraftBackup(workId: string): AutosavePayload | null {
  try {
    const raw = localStorage.getItem(backupKey(workId));
    if (!raw) return null;
    const parsed = JSON.parse(raw) as DraftBackup;
    if (!parsed || parsed.expiresAt <= Date.now() || typeof parsed.payload !== "object") {
      localStorage.removeItem(backupKey(workId));
      return null;
    }
    return parsed.payload;
  } catch {
    localStorage.removeItem(backupKey(workId));
    return null;
  }
}

function saveDraftBackup(workId: string, payload: AutosavePayload): void {
  const backup: DraftBackup = { expiresAt: Date.now() + backupTtlMs, payload };
  localStorage.setItem(backupKey(workId), JSON.stringify(backup));
}

export function useAutosave(workId: string, payload: AutosavePayload) {
  const [status, setStatus] = useState<AutosaveStatus>("idle");
  const payloadRef = useRef(payload);
  const versionRef = useRef(0);
  const timerRef = useRef<number | null>(null);

  const persist = useCallback(async (next: AutosavePayload, version: number) => {
    setStatus("saving");
    try {
      // 409 AUTOSAVE_CONFLICT = 다른 저장이 먼저 끝났다 — 아직 최신이면 지금 값(payloadRef)으로 다시 보낸다
      let body = next;
      const response = await retryOnAutosaveConflict(async () => {
        const sent = await apiRequest<AutosaveResponse>(`/api/works/${encodeURIComponent(workId)}/autosave`, {
          method: "POST",
          body: JSON.stringify({ ...body, gifts_v: 2 }),
        });
        return sent;
      }, () => {
        body = payloadRef.current;
        return version === versionRef.current;
      });
      // 쉬는 사이 더 새 저장이 생겼다 — 이 저장은 끝나지 않았다. 성공으로 끝내면 flush 를 기다린 「노래 만들기」가
      // 서버에 남은 옛 입력으로 진행한다(Codex 재검수). 저장 실패와 같은 길로 보낸다.
      if (!response) throw new Error("autosave superseded by a newer save");
      publishPendingGifts(response.pending_gifts);
      if (version === versionRef.current) setStatus("saved");
    } catch (error) {
      if (version === versionRef.current) setStatus("error");
      throw error;
    }
  }, [workId]);

  useEffect(() => {
    payloadRef.current = payload;
    versionRef.current += 1;
    const version = versionRef.current;
    saveDraftBackup(workId, payload);
    if (timerRef.current !== null) window.clearTimeout(timerRef.current);
    timerRef.current = window.setTimeout(() => {
      timerRef.current = null;
      void persist(payloadRef.current, version).catch(() => undefined);
    }, 30_000);
    return () => {
      if (timerRef.current !== null) window.clearTimeout(timerRef.current);
    };
  }, [payload, persist, workId]);

  const flush = useCallback(async (next: AutosavePayload = payloadRef.current) => {
    if (timerRef.current !== null) {
      window.clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    payloadRef.current = next;
    versionRef.current += 1;
    saveDraftBackup(workId, next);
    await persist(next, versionRef.current);
  }, [persist, workId]);

  return { status, flush };
}
